import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { FiLogOut, FiPlus, FiEdit2, FiTrash2, FiEye, FiCalendar, FiFileText, FiActivity, FiInbox, FiCheckCircle, FiFile } from 'react-icons/fi';
import ServicesManager from '../components/admin/ServicesManager';
import BookingsManager from '../components/admin/BookingsManager';
import '../styles/AdminDashboard.css';

import addInitialBlogs from '../utils/addInitialBlogs';

const AdminDashboard = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    // Check path for access mode
    const isBlogMode = window.location.pathname.includes('/blog') ||
        window.location.pathname.includes('/dashbord/blog');

    // Check session verification to force login form as requested
    const isBlogVerified = sessionStorage.getItem('blogAccessVerified') === 'true';
    const isBookingVerified = sessionStorage.getItem('bookingAccessVerified') === 'true';

    const [activeView, setActiveView] = useState(isBlogMode ? 'blogs' : 'bookings');

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            if (currentUser) {
                // Determine if we need to force re-login for THIS specific session
                const needsVerification = isBlogMode ? !isBlogVerified : !isBookingVerified;

                if (needsVerification) {
                    navigate('/admin/login', { state: { from: window.location.pathname } });
                    return;
                }

                setUser(currentUser);
                fetchBlogs();
            } else {
                navigate('/admin/login', { state: { from: window.location.pathname } });
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    useEffect(() => {
        if (user && activeView === 'blogs') {
            fetchBlogs();
        }
    }, [activeView, user]);

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            const blogsCollection = collection(db, 'blogs');
            let blogsSnapshot = await getDocs(blogsCollection);
            let blogsList = blogsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // If Firestore blog database is empty, seed initial blogs
            if (blogsList.length === 0) {
                console.log('Firestore blog database is empty. Auto-seeding initial blogs...');
                await addInitialBlogs();
                const freshSnapshot = await getDocs(blogsCollection);
                blogsList = freshSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
            }

            // Safe timestamp sorting
            blogsList.sort((a, b) => {
                const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
                const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                return timeB - timeA;
            });

            setBlogs(blogsList);
        } catch (error) {
            console.error('Error fetching blogs:', error);
        } finally {
            setLoading(false);
        }
    };

    // ... logic for logout, delete, format ...

    const handleLogout = async () => {
        try {
            await signOut(auth);
            sessionStorage.removeItem('blogAccessVerified');
            sessionStorage.removeItem('bookingAccessVerified');
            navigate('/admin/login');
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const handleDelete = async (blogId) => {
        if (window.confirm('Are you sure you want to delete this blog?')) {
            try {
                await deleteDoc(doc(db, 'blogs', blogId));
                setBlogs(blogs.filter(blog => blog.id !== blogId));
                alert('Blog deleted successfully!');
            } catch (error) {
                console.error('Error deleting blog:', error);
                alert('Error deleting blog. Please try again.');
            }
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <div className="admin-header-content">
                    <div>
                        <h1>{isBlogMode ? 'Blog & Services Management' : 'Bookings Panel'}</h1>
                    </div>
                    <div className="admin-header-actions">
                        <button
                            className="btn-logout"
                            onClick={handleLogout}
                        >
                            <FiLogOut /> Logout
                        </button>
                    </div>
                </div>
            </div>

            {isBlogMode && (
                <div className="admin-nav-container" style={{ maxWidth: '1400px', margin: '30px auto 0', padding: '0 30px' }}>
                    <div className="admin-tabs" style={{
                        display: 'inline-flex',
                        background: 'white',
                        padding: '6px',
                        borderRadius: '12px',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                        marginBottom: '20px'
                    }}>
                        <button
                            onClick={() => setActiveView('blogs')}
                            style={{
                                padding: '12px 30px',
                                border: 'none',
                                background: activeView === 'blogs' ? 'var(--primary)' : 'transparent',
                                color: activeView === 'blogs' ? 'white' : '#666',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FiFileText size={18} /> Blog Management
                        </button>
                        <button
                            onClick={() => setActiveView('services')}
                            style={{
                                padding: '12px 30px',
                                border: 'none',
                                background: activeView === 'services' ? 'var(--primary)' : 'transparent',
                                color: activeView === 'services' ? 'white' : '#666',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FiActivity size={18} /> Services (Home)
                        </button>
                    </div>
                </div>
            )}

            <div className="admin-content">
                {!isBlogMode || activeView === 'bookings' ? (
                    <BookingsManager />
                ) : activeView === 'blogs' ? (
                    <>
                        <div className="stats-cards">
                            <div className="stat-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <h3>Total Blogs</h3>
                                        <p className="stat-number">{blogs.length}</p>
                                    </div>
                                    <div style={{ background: '#e0f2fe', color: '#0ea5e9', padding: '12px', borderRadius: '12px' }}>
                                        <FiInbox size={24} />
                                    </div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <h3>Published</h3>
                                        <p className="stat-number">{blogs.filter(b => b.status === 'published').length}</p>
                                    </div>
                                    <div style={{ background: '#dcfce7', color: '#22c55e', padding: '12px', borderRadius: '12px' }}>
                                        <FiCheckCircle size={24} />
                                    </div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div>
                                        <h3>Drafts</h3>
                                        <p className="stat-number">{blogs.filter(b => b.status === 'draft').length}</p>
                                    </div>
                                    <div style={{ background: '#fef3c7', color: '#f59e0b', padding: '12px', borderRadius: '12px' }}>
                                        <FiFile size={24} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="blogs-table-container">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h2>All Blogs</h2>
                                <button
                                    className="btn-primary"
                                    onClick={() => navigate('/admin/blog/new')}
                                >
                                    <FiPlus /> Add New Blog
                                </button>
                            </div>
                            {blogs.length === 0 ? (
                                <div className="empty-state">
                                    <p>No blogs found. Create your first blog!</p>
                                </div>
                            ) : (
                                <div className="blogs-grid">
                                    {blogs.map((blog) => (
                                        <div key={blog.id} className="blog-card">
                                            {(blog.imageUrl || blog.image) && (
                                                <div className="blog-card-image">
                                                    <img src={blog.imageUrl || blog.image} alt={blog.title} />
                                                    <span className={`status-badge ${blog.status}`}>
                                                        {blog.status || 'published'}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="blog-card-content">
                                                <h3>{blog.title}</h3>
                                                <p className="blog-excerpt">
                                                    {blog.excerpt || blog.content?.substring(0, 100) + '...'}
                                                </p>
                                                <div className="blog-meta">
                                                    <span className="blog-date">{formatDate(blog.createdAt)}</span>
                                                    <span className="blog-author">{blog.author || 'Admin'}</span>
                                                </div>
                                                <div className="blog-actions">
                                                    <button
                                                        className="btn-icon btn-view"
                                                        onClick={() => navigate(`/blogs/${blog.id}`)}
                                                        title="View"
                                                    >
                                                        <FiEye />
                                                    </button>
                                                    <button
                                                        className="btn-icon btn-edit"
                                                        onClick={() => navigate(`/admin/blog/edit/${blog.id}`)}
                                                        title="Edit"
                                                    >
                                                        <FiEdit2 />
                                                    </button>
                                                    <button
                                                        className="btn-icon btn-delete"
                                                        onClick={() => handleDelete(blog.id)}
                                                        title="Delete"
                                                    >
                                                        <FiTrash2 />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <ServicesManager />
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
